import React, { useState, useEffect } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Calendar, TrendingUp, Users, CheckCircle2 } from 'lucide-react';

const AdminCheckins = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await apiClient.get('/admin/checkins-history?days=30');
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch check-in history:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const totalCheckins = history.reduce((sum, day) => sum + day.total_checkins, 0);
  const totalPoints = history.reduce((sum, day) => sum + day.total_points, 0);
  const avgUsersPerDay = history.length > 0 
    ? Math.round(history.reduce((sum, day) => sum + day.unique_users_count, 0) / history.length)
    : 0;

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 flex items-center gap-3">
            <Calendar className="text-[#EFB342]" />
            CHECK-IN HISTORY
          </h1>
          <p className="text-stone-600 mt-1">Last 30 days of activity</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="text-green-500" size={24} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{totalCheckins}</p>
                <p className="text-xs text-stone-500">Total Check-ins</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="text-[#EFB342]" size={24} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{totalPoints}</p>
                <p className="text-xs text-stone-500">Total Points</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Users className="text-purple-500" size={24} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{avgUsersPerDay}</p>
                <p className="text-xs text-stone-500">Avg Users/Day</p>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Breakdown */}
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left p-4 text-xs text-stone-500 uppercase font-semibold">Date</th>
                  <th className="text-center p-4 text-xs text-stone-500 uppercase font-semibold">Check-ins</th>
                  <th className="text-center p-4 text-xs text-stone-500 uppercase font-semibold">Unique Users</th>
                  <th className="text-center p-4 text-xs text-stone-500 uppercase font-semibold">Points Earned</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {history.length > 0 ? (
                  history.map((day) => (
                    <tr key={day._id} className="hover:bg-[#F0EBE1] transition-colors">
                      <td className="p-4">
                        <span className="text-stone-900 font-medium">
                          {new Date(day._id).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-medium text-stone-900">{day.total_checkins}</span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="text-stone-600">{day.unique_users_count}</span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-bold text-[#EFB342]">{day.total_points}</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-stone-500">
                      No check-in data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminCheckins;
